import { useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { orderBy } from 'lodash';

import {
  useCurrentEntertainerQuery,
  useEntertainerByBookingHandleQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';
import { useCurrentUser, useUserWalletQuery } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useIsVisible } from '@hooks/useIsVisible';
import { useShareLink } from '@hooks/useShareLink';

import { useRouter } from '@router/index';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';

const InitialStateContent = () => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();

  const handleAddShow = () => {
    track('Tip jar add show');
    navigate('/manage/schedule-submissions');
  };

  return (
    <div className="d-flex gap-3 align-items-center" onClick={handleAddShow}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{ fontSize: 44, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-yellow)' }}
        >
          $10
        </div>
        <div style={{ opacity: 0.8, fontSize: 12 }}>per show</div>
      </div>
      <div>
        <div>
          <div style={{ fontSize: 18 }} className="mb-1">
            <strong>Earn up to $10 for every show you add</strong>
          </div>
          <div style={{ color: '#999', fontSize: 13 }}>
            Share a show's invite link with your cast. You get $5 each for the first 2 castmates who
            join.
          </div>
        </div>
        <div className="mt-2">
          <div className="d-inline-block">
            <Button
              color="yellow"
              roundness="rounded"
              size="sm"
              className="px-3"
              endIcon={<FontAwesomeIcon name="chevron-right" />}
            >
              Add your next show
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dots = ({ completedCount }: { completedCount: number }) => {
  return (
    <div className="d-flex align-items-center gap-1">
      {Array.from({ length: 2 }).map((_, index) => (
        <div
          key={index}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: index < completedCount ? 'var(--color-yellow)' : 'transparent',
            border:
              index < completedCount ? '1.5px dashed var(--color-yellow)' : '1.5px dashed #8a7433',
          }}
        />
      ))}
    </div>
  );
};

const QualifiedStateContentShowItem = ({
  show,
}: {
  show: {
    _id: string;
    title: string;
    startDate: string;
    referralLink?: string;
    referrals: { createdAt: string; entertainer: { _id: string; title: string } }[];
  };
}) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();

  const { shareLink, didCopy } = useShareLink();

  const handleCopyLinkClick = () => {
    if (show.referralLink) {
      track('Click tip jar copy link');
      shareLink({ url: show.referralLink });
    }
  };

  const handleAddDetailsClick = (date: string) => {
    if (date) {
      track('Click tip jar add details');
      navigate(`/manage/schedule-submissions?addDateDetails=${date}`);
    }
  };

  return (
    <div className="py-2" style={{ borderTop: '1px solid rgba(255,255,255,.2)' }}>
      <div className="d-flex gap-2 align-items-center">
        <div
          style={{
            width: 40,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.08,
              color: 'var(--color-yellow)',
              textTransform: 'uppercase',
            }}
          >
            {dayjs(show.startDate).format('MMM')}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {dayjs(show.startDate).format('D')}
          </div>
          <div
            style={{
              fontSize: 10,
              color: '#cfc6ad',
            }}
          >
            {dayjs(show.startDate).format('ddd')}
          </div>
        </div>
        <div className="d-grid w-100">
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {show.title ?? 'Details not provided'}
          </div>
          <div className="d-flex align-items-center gap-1">
            {show.title ? (
              show.referralLink ? (
                show.referrals.length < 2 ? (
                  <>
                    <div className="me-1">
                      <Dots completedCount={show.referrals.length} />
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>{show.referrals.length} of 2</div>
                    <div style={{ fontSize: 12, color: '#999' }}>&bull;</div>
                    <div
                      style={{ fontSize: 12, color: '#999' }}
                    >{`$${10 - show.referrals.length * 5} left`}</div>
                  </>
                ) : (
                  <>
                    <div className="me-1">
                      <Dots completedCount={show.referrals.length} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-yellow)' }}>
                      2 of 2 · Maxed out
                    </div>
                  </>
                )
              ) : (
                <div style={{ fontSize: 12, color: '#999' }}>Checking · up to $10 once live</div>
              )
            ) : (
              <div style={{ fontSize: 12, color: '#999' }}>Add info to earn up to $10</div>
            )}
          </div>
        </div>
        <div>
          {show.title ? (
            show.referralLink ? (
              show.referrals.length < 2 ? (
                <Button
                  color="yellow"
                  roundness="rounded"
                  size="sm"
                  className="px-2"
                  endIcon={<FontAwesomeIcon name="share-square" />}
                  onClick={handleCopyLinkClick}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>{didCopy ? 'Copied' : 'Copy link'}</span>
                </Button>
              ) : (
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-yellow)' }}
                >
                  <div>
                    <FontAwesomeIcon name="check" />
                  </div>
                  <span style={{ whiteSpace: 'nowrap' }}>$10 earned</span>
                </div>
              )
            ) : (
              <Button
                color="yellow"
                variant="outlined"
                roundness="rounded"
                size="sm"
                className="px-2"
                onClick={handleCopyLinkClick}
              >
                <span style={{ whiteSpace: 'nowrap' }}>Pending</span>
              </Button>
            )
          ) : (
            <Button
              color="yellow"
              variant="outlined dashed"
              roundness="rounded"
              size="sm"
              className="px-2"
              onClick={() => handleAddDetailsClick(show.startDate)}
              startIcon={<FontAwesomeIcon name="plus" />}
            >
              <span style={{ whiteSpace: 'nowrap' }}>Details</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const QualifiedStateContent = ({
  entertainerEventReferralOpportunities,
}: {
  entertainerEventReferralOpportunities: {
    _id: string;
    title: string;
    startDate: string;
    referralLink?: string;
    referrals: { createdAt: string; entertainer: { _id: string; title: string } }[];
  }[];
}) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();

  const areAllOpportunitiesPending = useMemo(() => {
    return entertainerEventReferralOpportunities.every((opportunity) => !opportunity.referralLink);
  }, [entertainerEventReferralOpportunities]);

  const possibleEarnings = useMemo(() => {
    return entertainerEventReferralOpportunities.reduce(
      (acc, opportunity) => acc + (2 - opportunity.referrals.length) * 5,
      0,
    );
  }, [entertainerEventReferralOpportunities]);

  const latestReferral = useMemo(() => {
    return orderBy(
      entertainerEventReferralOpportunities.flatMap((opportunity) =>
        opportunity.referrals.map((referral) => ({
          ...referral,
          opportunityTitle: opportunity.title,
        })),
      ),
      'createdAt',
      'desc',
    )[0];
  }, [entertainerEventReferralOpportunities]);

  const title = useMemo(() => {
    if (areAllOpportunitiesPending) {
      if (entertainerEventReferralOpportunities.length === 1) {
        return `${entertainerEventReferralOpportunities?.[0]?.title ?? 'Your show'} is being checked`;
      }

      return `Your shows are being checked`;
    }

    if (latestReferral) {
      return (
        <span>
          <span>{`${latestReferral.entertainer.title} joined ${latestReferral.opportunityTitle},`}</span>{' '}
          <span style={{ color: 'var(--color-yellow)' }}>+$5</span>
        </span>
      );
    }

    return (
      <span>
        <span style={{ color: 'var(--color-yellow)' }}>{`$${possibleEarnings}`}</span> waiting
        across your shows
      </span>
    );
  }, [possibleEarnings, entertainerEventReferralOpportunities, areAllOpportunitiesPending]);

  const description = useMemo(() => {
    if (areAllOpportunitiesPending) {
      if (entertainerEventReferralOpportunities.length === 1) {
        return `Its invite link goes live soon. Every other show you add is up to $10 more.`;
      }

      return `Your invite links go live soon. Every other show you add is up to $10 more.`;
    }

    if (latestReferral) {
      if (possibleEarnings) {
        return `You can still earn up to $${possibleEarnings} for the other shows you've added.`;
      } else {
        return `Have more shows coming up? Add them to earn up to $10 more for each.`;
      }
    }

    return `$5 each for the first 2 castmates who join from a show's link.`;
  }, [possibleEarnings, entertainerEventReferralOpportunities, latestReferral]);

  const handleManageScheduleClick = () => {
    track('Click tip jar manage schedule');
    navigate('/manage/schedule-submissions');
  };

  const handleSeeAllShowsClick = () => {
    track('Click tip jar see all shows');
    navigate('/manage/schedule-submissions');
  };

  return (
    <div className="mt-2">
      <div className="d-flex gap-3 align-items-center">
        <div>
          <div style={{ fontSize: 18 }} className="mb-1">
            <strong>{title}</strong>
          </div>
          <div style={{ color: '#999', fontSize: 13 }}>{description}</div>
        </div>
      </div>
      <div
        className="d-grid mt-3"
        style={{
          borderBottom: '1px solid rgba(255,255,255,.2)',
        }}
      >
        {entertainerEventReferralOpportunities.map((show) => (
          <QualifiedStateContentShowItem key={`${show.startDate}-${show.title}`} show={show} />
        ))}
      </div>
      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="d-inline-block">
              <Button
                color="yellow"
                size="sm"
                className="py-2 px-3"
                startIcon={<FontAwesomeIcon name="plus" />}
                onClick={handleManageScheduleClick}
              >
                Manage schedule
              </Button>
            </div>
          </div>
          <div className="ml-auto">
            <Button
              color="yellow"
              variant="flat"
              size="sm"
              className="py-2 px-3"
              endIcon={<FontAwesomeIcon name="chevron-right" />}
              onClick={handleSeeAllShowsClick}
            >
              See all shows
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TipJarSection = () => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { walletIsReady, wallet } = useUserWalletQuery();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(containerRef);
  const {
    refetchEntertainer,
    entertainer: currentEntertainer,
    upcomingEvents,
    upcomingScheduleDateDetails,
    entertainerEventReferralOpportunities,
  } = useCurrentEntertainerQuery({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isVisible) {
      refetchEntertainer();
    }
  }, [isVisible]);

  const walletBalanceFormatted = useMemo(() => {
    if (!wallet) {
      return '$0';
    }
    return wallet.total % 100
      ? `$${(wallet.total / 100).toFixed(2)}`
      : `$${(wallet.total / 100).toFixed(0)}`;
  }, [wallet]);

  const handleEarningsClick = () => {
    track('Click tip jar earnings');
    navigate('/profile');
  };

  return (
    <div
      ref={containerRef}
      style={{
        border: '1px solid var(--color-yellow)',
        backgroundColor: 'rgba(255, 204, 0, 0.1)',
        borderRadius: 16,
        padding: '12px 16px 16px',
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          style={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: 12,
            color: 'var(--color-yellow)',
          }}
        >
          Gaggl Tip Jar
        </div>
        {!!wallet?.total && (
          <div
            className="d-flex align-items-center gap-2"
            onClick={handleEarningsClick}
            style={{
              fontSize: 11,
              background: '#2c2412',
              borderRadius: 500,
              fontWeight: 600,
              padding: '4px 10px',
            }}
          >
            <div>{walletBalanceFormatted} earned</div>
            <div>
              <FontAwesomeIcon name="chevron-right" />
            </div>
          </div>
        )}
      </div>
      {Boolean(entertainerEventReferralOpportunities?.length) ? (
        <QualifiedStateContent
          entertainerEventReferralOpportunities={entertainerEventReferralOpportunities}
        />
      ) : (
        <InitialStateContent />
      )}
    </div>
  );
};
